import React, { useEffect, useMemo, useRef, useState } from "react";

import { useGetObjectDataset } from "api/api-hooks/object-dataset-hooks";
import { Dropdown } from "primereact/dropdown";
import { useUserState } from "store/user-store";
import { ObjectDatasetDataDto } from "api/model/object-dataset/types";
import { getLocale } from "utils/utils";
import { DEFAULT_DATE_PATTERN } from "utils/constants";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { ProgressSpinner } from "primereact/progressspinner";
import { T } from "services/lang/LanguageService";
import DatePicker from "react-datepicker";
import { isSameDay, parseISO } from "date-fns";
import { ObjectDatasetConfig } from "./ObjectDatasetConfig";
import "react-datepicker/dist/react-datepicker.css";
import "./ObjectDataset.scss";

export default function ObjectDataset(props: ObjectDatasetConfig) {
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [objectData, setObjectData] = useState<ObjectDatasetDataDto | null>(null);
  const [content, setContent] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const userLang = useUserState.getState().currentUser?.preferences.language;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const company = useCurrentCompanyContext();

  const { data } = useGetObjectDataset(props.identifier ?? "", company?.companyReference ?? "", {
    enabled: !!company?.companyReference || !!props.identifier,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data && data.data?.length > 0) {
      const sortData = data.data.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      const latest = sortData[0];
      setObjectData(latest);
      setSelectedDate(parseISO(latest.timestamp));
    }
  }, [data]);

  useEffect(() => {
    if (objectData) {
      const foundFile = objectData.fileNames.find(
        file => props.initialFileName && file === props.initialFileName
      );
      setContent(foundFile ?? objectData.fileNames[0]);
    }
  }, [objectData, props.initialFileName]);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      const iframeDoc = iframe.contentWindow.document;
      const height = Array.from(iframeDoc.body.children).reduce((acc, child) => {
        return acc + child.scrollHeight;
      }, 0);
      iframe.style.height = `${height + 40}px`;
    }
    setIsLoadingFile(false);
  };

  const fileUrl = encodeURI(
    `${window.contextRoot}/rest/object-dataset/${company?.companyReference}/${objectData?.identifier}/${objectData?.timestamp}/${content}`
  );
  const toLocalDate = (iso: string) => {
    const utc = parseISO(iso);
    return new Date(utc.getTime() + utc.getTimezoneOffset() * 60000 * -1);
  };

  const availableTimestamps = useMemo(() => {
    return (data?.data ?? []).map(d => ({
      date: toLocalDate(d.timestamp),
      item: d,
    }));
  }, [data]);

  const availableDates = useMemo(() => {
    return [...new Set(availableTimestamps.map(d => d.date.toDateString()))].map(d => new Date(d));
  }, [availableTimestamps]);

  const timesForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];

    return availableTimestamps
      .filter(d => isSameDay(d.date, selectedDate))
      .map(d => new Date(d.date));
  }, [availableTimestamps, selectedDate]);

  const handleOnDateChange = (date: Date | null) => {
    setSelectedDate(date);
    const matched = availableTimestamps.find(d => d.date.getTime() === date?.getTime());
    if (matched) {
      setObjectData(matched.item);
      setContent(matched.item.fileNames[0]);
    }
  };

  return (
    <div className="flex flex-wrap gap-3">
      <div className="flex gap-2 w-full">
        <DatePicker
          selected={selectedDate}
          onChange={handleOnDateChange}
          showTimeSelect
          includeDates={availableDates}
          includeTimes={timesForSelectedDate}
          dateFormat={DEFAULT_DATE_PATTERN}
          placeholderText={T("select.date-and-time")}
          locale={getLocale(userLang)}
          timeIntervals={1}
        />
        {objectData && objectData.fileNames.length > 1 && data?.type !== "HTML" && (
          <Dropdown
            options={objectData.fileNames}
            value={content}
            onChange={e => setContent(e.value)}
          />
        )}
      </div>
      {isLoadingFile && (
        <div className="w-full align-self-center">
          <ProgressSpinner style={{ width: "10rem" }} />
        </div>
      )}
      {!content && (
        <div className="text-sm text-gray-500 align-self-center my-5">{T("message.no-models")}</div>
      )}
      {content && data?.type === "IMAGE" && (
        <img
          src={fileUrl}
          alt="model-view"
          loading="lazy"
          onLoad={() => setIsLoadingFile(false)}
          style={{ objectFit: "contain", width: "100%", height: "auto" }}
        />
      )}
      {content && data?.type !== "IMAGE" && (
        <iframe
          ref={iframeRef}
          loading="lazy"
          src={fileUrl}
          title="model-view"
          onLoad={handleIframeLoad}
          style={{ maxHeight: "200vh" }}
          className="flex-1 border-none"
        />
      )}
    </div>
  );
}
